import React from 'react';
import { twMerge } from 'tailwind-merge';
import { Body2, gtPlanar } from 'components/Typography';
import { cva, VariantProps } from 'class-variance-authority';

const variants = cva(
  'flex flex-row text-nowrap items-center justify-center rounded border-[1px] hover:bg-linear-gradient-black border-[#48484E] px-4 py-2',
  {
    variants: {
      active: {
        true: 'bg-[#1E1E1E]',
        false: 'bg-black',
      },
    },
  }
);

interface SlippageButtonProps extends VariantProps<typeof variants> {
  className?: string;
  label: string;
  value: number;
  onClick: (value: number) => void;
}

export const SlippageButton = (props: SlippageButtonProps) => {
  const { className, value, active, label, onClick } = props;

  const classNames = twMerge(variants({ active }), className);

  return (
    <button type='button' className={classNames} onClick={() => onClick(value)}>
      <Body2>{label}</Body2>
    </button>
  );
};

interface SlippageInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  onChangeSlippage: (value: number) => void;
}

export const SlippageInput = React.forwardRef(function (
  props: SlippageInputProps,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  const { value, onChangeSlippage, ...rest } = props;

  const handleChangeValue = (value: number) => {
    onChangeSlippage(value);
  };

  const options = [
    { label: '0.1 %', value: 0.1 },
    { label: '0.5 %', value: 0.5 },
    { label: '1.0 %', value: 1 },
  ];

  return (
    <div className='flex flex-row gap-3'>
      {options.map((option) => {
        return (
          <SlippageButton
            className='shrink'
            key={value + option.label}
            active={value === option.value}
            label={option.label}
            value={option.value}
            onClick={handleChangeValue}
          />
        );
      })}
      <BaseInput
        type='number'
        step='0.1'
        ref={ref}
        {...rest}
        endEnhancer={<div>%</div>}
      />
    </div>
  );
});

interface BaseInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  startEnhancer?: React.ReactNode;
  ref?: React.ForwardedRef<HTMLInputElement>;
  endEnhancer?: React.ReactNode;
}

const BaseInput = React.forwardRef<HTMLInputElement, BaseInputProps>(
  (props, ref) => {
    const { startEnhancer, endEnhancer, ...rest } = props;

    return (
      <div className='flex flex-row gap-1 rounded border-[0.6px] border-[#48484E] bg-[#1E1E1E] px-3 py-2 text-white'>
        {startEnhancer ? startEnhancer : null}
        <input
          type='number'
          className={`${gtPlanar.className} w-full min-w-0 max-w-full bg-inherit text-[0.875rem] text-white focus-visible:outline-0`}
          ref={ref}
          {...rest}
        />
        {endEnhancer ? endEnhancer : null}
      </div>
    );
  }
);
